<template>
  <div class="reference-card-expanded">
    <div class="main-content flex-grow-1">
      <div class="d-flex justify-end px-3 pt-2">
        <v-btn
          v-if="canMarkAsDuplicate"
          small
          text
          color="primary"
          @click="$emit('mark-as-duplicate', reference)"
        >
          <v-icon left small>content_copy</v-icon>
          Mark as duplicate
        </v-btn>
      </div>
      <page-tabs
        v-model="selectedTabIdx"
        :tabs="tabsToShow"
        class="px-3 pb-3"
        value-prop="label"
        left-pad="0"
        dense
      >
        <page-tab-panel>
          <div class="d-flex justify-center">
            <abstract-content
              v-if="hasAbstract"
              :abstract="reference.publication.abstract"
              :search-terms="highlightTerms"
            />
            <missing-abstract-chip
              v-else
              @click="$emit('edit', 'abstract')"
            />
          </div>
        </page-tab-panel>
        <page-tab-panel>
          <template v-if="reference.hasFullTextPdf()">
            <div class="pt-3 fill-height">
              <full-text-pdf-preview :reference="reference" />
            </div>
          </template>
          <template v-else>
            <upload-full-text-pdf-controller
              class="pt-5"
              :reference="reference"
            />
          </template>
        </page-tab-panel>
      </page-tabs>
    </div>
    <reference-details-section
      class="reference-details"
      :reference="reference"
      :review="review"
      :current-researcher="researcher"
      :allow-full-text-upload="allowFullTextUpload"
      :tab-order="['INFO', 'COMMENTS', 'EVENTS']"
      allow-edit
      @edit="$emit('edit', $event)"
      @download-full-text="$emit('download-full-text', $event)"
      @delete-full-text="$emit('delete-full-text', $event)"
      @update:hasUnsavedComment="
        $emit('update:hasUnsavedComment', $event)
      "
      @update-tags="$emit('update-tags', $event)"
    />
    <reference-action-controls
      class="actions"
      :review="review"
      :reference="reference"
      :current-review-member="currentReviewMember"
      :disabled="disableActions"
      @create-screening-vote="$emit('create-screening-vote', $event)"
      @change-screening-vote="$emit('change-screening-vote', $event)"
      @revoke-screening-vote="$emit('revoke-screening-vote', $event)"
      @resolve-screening-conflict="
        $emit('resolve-screening-conflict', $event)
      "
      @change-screening-outcome="
        $emit('change-screening-outcome', $event)
      "
      @create-full-text-review-vote="
        $emit('create-full-text-review-vote', $event)
      "
      @change-full-text-review-vote="
        $emit('change-full-text-review-vote', $event)
      "
      @revoke-full-text-review-vote="
        $emit('revoke-full-text-review-vote', $event)
      "
      @resolve-full-text-review-conflict="
        $emit('resolve-full-text-review-conflict', $event)
      "
      @change-full-text-review-outcome="
        $emit('change-full-text-review-outcome', $event)
      "
      @extract="$emit('extract', $event)"
      @extraction-consensus-review="
        $emit('extraction-consensus-review', $event)
      "
    />
  </div>
</template>

<script>
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import AbstractContent from '@/components/Common/AbstractContent';
import MissingAbstractChip from '@/components/References/MissingAbstractChip';
import FullTextPdfPreview from '@/components/References/FullTextPdfPreview';
import UploadFullTextPdfController from '@/components/UploadFullTextPdfController/UploadFullTextPdfController';
import ReferenceDetailsSection from '@/components/References/ReferenceDetailsSection';
import ReferenceActionControls from './ReferenceActionControls/ReferenceActionControls';
import { Reference, Review, Researcher } from '@/models';
import { ReviewStage } from '@/enums';

export default {
  name: 'ReferenceCardExpanded',
  components: {
    PageTabs,
    PageTabPanel,
    AbstractContent,
    MissingAbstractChip,
    FullTextPdfPreview,
    UploadFullTextPdfController,
    ReferenceDetailsSection,
    ReferenceActionControls,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    review: {
      type: Review,
      required: true,
    },
    researcher: {
      type: Researcher,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    highlightTerms: {
      type: Array,
      default: () => [],
    },
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'collapse',
    'edit',
    'download-full-text',
    'delete-full-text',
    'update:hasUnsavedComment',
    'update-tags',
    'create-screening-vote',
    'change-screening-vote',
    'revoke-screening-vote',
    'resolve-screening-conflict',
    'change-screening-outcome',
    'create-full-text-review-vote',
    'change-full-text-review-vote',
    'revoke-full-text-review-vote',
    'resolve-full-text-review-conflict',
    'change-full-text-review-outcome',
    'extract',
    'extraction-consensus-review',
    'mark-as-duplicate',
  ],
  data: () => ({
    selectedTabIdx: 0,
  }),
  computed: {
    tabsToShow() {
      const tabs = [{ label: 'Abstract' }];
      if (this.fullTextReviewAllowed) {
        tabs.push({ label: 'Full Text PDF' });
      }
      return tabs;
    },
    hasAbstract() {
      const abstract = this.reference.publication.abstract;
      return abstract && abstract.trim().length > 0;
    },
    showingFullTextTab() {
      return this.selectedTabIdx === 1;
    },
    fullTextReviewAllowed() {
      return (
        this.reference.getReviewStage() !== ReviewStage.SCREENING
      );
    },
    allowFullTextUpload() {
      return this.fullTextReviewAllowed && !this.showingFullTextTab;
    },
    currentReviewMember() {
      return this.review.reviewMembers.find(
        (member) => member.researcher.id === this.researcher.id
      );
    },
    canMarkAsDuplicate() {
      return !!this.review?.currentUserCanEditReview?.();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.reference-card-expanded {
  display: grid;
  grid-template-columns: 1fr 400px;

  border-top: 1px solid map-get($grey, 'lighten-2');

  .main-content {
    display: flex;
    flex-direction: column;
    border-right: 1px solid map-get($grey, 'lighten-2');
  }

  .actions {
    grid-column: span 2;
  }
}
</style>
